import useSWR from "swr";
import { useSession } from "next-auth/react";
import { useEffect, useRef } from "react";
import { getCurrentUserInfo } from "@/app/lib/unified-api";
import { useUserRole } from "@/app/hooks/useUserRole";
import { UserResponse } from "@/app/types";

/**
 * Hook for fetching current user info with caching.
 *
 * Features:
 * - Only fetches when authenticated
 * - Polls every 30s when user is NOT verified (to detect verification in another tab)
 * - Auto-updates NextAuth session when backend isActive changes
 * - Revalidates on focus for better UX
 */
export function useCurrentUser() {
  const { data: session, status, update: updateSession } = useSession();
  const { isAdmin } = useUserRole();
  const lastSyncedIsActive = useRef<boolean | null>(null);

  // Only fetch if authenticated
  const shouldFetch = status === "authenticated";

  // Poll more frequently if user is not verified (to detect verification in another tab)
  // Once verified, stop polling (refreshInterval: 0)
  const isVerified = session?.user?.isActive ?? false;
  const refreshInterval = shouldFetch && !isVerified ? 30000 : 0;

  const result = useSWR<UserResponse>(
    shouldFetch ? "current-user" : null,
    () => getCurrentUserInfo(),
    {
      revalidateOnFocus: true, // Revalidate when user returns to tab
      refreshInterval,
      dedupingInterval: 10000, // 10 second deduping (reduced from 5 minutes)
    }
  );

  // Auto-update session when backend isActive status changes
  useEffect(() => {
    if (!result.data || status !== "authenticated") return;

    const backendIsActive = result.data.isActive;
    const sessionIsActive = session?.user?.isActive;

    // Skip if we already synced this value (prevents loops)
    if (lastSyncedIsActive.current === backendIsActive) return;

    // If backend says verified but session doesn't, update session
    if (backendIsActive && !sessionIsActive) {
      lastSyncedIsActive.current = backendIsActive;
      updateSession({ user: { isActive: true } });
    }
  }, [result.data, session?.user?.isActive, status, updateSession]);

  // Prefer backend data over session for isEmailVerified (more up-to-date)
  const isEmailVerified = result.data?.isActive ?? session?.user?.isActive ?? false;

  return {
    ...result,
    user: result.data ?? null,
    isAdmin,
    isAuthenticated: status === "authenticated",
    isLoading: status === "loading" || result.isLoading,
    isEmailVerified,
  };
}
